import React, { useMemo } from 'react';
import { Box, MenuItem, Select, Typography } from '@mui/material';
import Button from '../Button/Button';
import { useFeatureTheme } from '../../../theme/hooks/useFeatureTheme';

function getVisiblePages(page, totalPages, siblingCount = 1) {
  if (totalPages <= 1) {
    return [0];
  }

  const pages = new Set([0, totalPages - 1, page]);
  for (let i = page - siblingCount; i <= page + siblingCount; i += 1) {
    if (i >= 0 && i < totalPages) {
      pages.add(i);
    }
  }

  return Array.from(pages).sort((a, b) => a - b);
}

export default function TablePagination({
  page = 0,
  pageSize = 20,
  totalCount = 0,
  pageSizeOptions = [10, 20, 50],
  onPageChange,
  onPageSizeChange,
  sx = {},
}) {
  const { semantic, typography, radiusPx } = useFeatureTheme();

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize) || 1);
  const safePage = Math.min(Math.max(page, 0), totalPages - 1);
  const start = totalCount === 0 ? 0 : safePage * pageSize + 1;
  const end = totalCount === 0 ? 0 : Math.min((safePage + 1) * pageSize, totalCount);
  const visiblePages = useMemo(
    () => getVisiblePages(safePage, totalPages),
    [safePage, totalPages]
  );

  const handlePageClick = (nextPage) => {
    if (nextPage >= 0 && nextPage < totalPages && nextPage !== safePage) {
      onPageChange?.(nextPage);
    }
  };

  return (
    <Box
      data-testid="table-pagination"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        gap: 2,
        py: 0.5,
        ...sx,
      }}
    >
      <Typography sx={{ fontSize: typography.fontSizeSm, color: semantic.text.secondary }}>
        {totalCount === 0
          ? 'Showing 0 results'
          : `Showing ${start}–${end} of ${totalCount} results`}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', ml: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: typography.fontSizeSm, color: semantic.text.secondary }}>
            Rows
          </Typography>
          <Select
            size="small"
            value={pageSize}
            onChange={(event) => onPageSizeChange?.(Number(event.target.value))}
            sx={{
              minWidth: 72,
              fontSize: typography.fontSizeSm,
              borderRadius: radiusPx.md,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: semantic.borders.border,
              },
            }}
          >
            {pageSizeOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Button
            variant="ghost"
            size="sm"
            disabled={safePage <= 0}
            onClick={() => handlePageClick(safePage - 1)}
          >
            Previous
          </Button>

          {visiblePages.map((pageIndex, index) => {
            const prev = visiblePages[index - 1];
            const showEllipsis = index > 0 && pageIndex - prev > 1;

            return (
              <React.Fragment key={pageIndex}>
                {showEllipsis ? (
                  <Typography sx={{ px: 0.5, color: semantic.text.muted }}>…</Typography>
                ) : null}
                <Button
                  variant={pageIndex === safePage ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => handlePageClick(pageIndex)}
                  sx={{ minWidth: 36, px: 1.25, borderRadius: radiusPx.md }}
                >
                  {pageIndex + 1}
                </Button>
              </React.Fragment>
            );
          })}

          <Button
            variant="ghost"
            size="sm"
            disabled={safePage >= totalPages - 1}
            onClick={() => handlePageClick(safePage + 1)}
          >
            Next
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
